/**
 * Bearer-token plumbing.
 *
 * The SPA mints and owns its own bearer via `POST /v1/auth/login` (Stage A) and
 * persists it in `localStorage`. There is no PHP shell anymore, so there is no
 * `<meta name="api-token">` bootstrap and no CSRF — bearer auth is CSRF-immune
 * (browsers don't auto-attach `Authorization`).
 *
 * `localStorage` (not `sessionStorage`) keeps the user signed in across tab
 * closes and browser restarts — matching the old 30-day session. The
 * server-side `api_tokens` row carries a 30-day sliding `expires_at` (renewed on
 * every authenticated request), so the login lasts as long as it's used and only
 * ends when the user logs out (which revokes the token), goes 30 days idle, or
 * clears browser storage. Being shared across tabs also means logging in / out in
 * one tab reflects in the others.
 */

const API_TOKEN_STORAGE_KEY = 'api-token';

function readStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(API_TOKEN_STORAGE_KEY);
    return raw && raw.trim().length > 0 ? raw : null;
  } catch {
    return null;
  }
}

export function readApiToken(): string | null {
  return readStoredToken();
}

export function writeApiToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(API_TOKEN_STORAGE_KEY, token);
  } catch {
    // Quota / disabled storage — nothing else to fall back to.
  }
}

export function clearApiToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(API_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getAuthHeaders(): Record<string, string> {
  const token = readApiToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Custom event fired by the API client when a request comes back 401.
 * Routes listen via window.addEventListener('auth:unauthorized', ...).
 */
export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

export function dispatchUnauthorized(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
}
